<template>
  <Page :page-name="'User List'">
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <Toolbar pt:root:class="mx-4 py-2">
        <template #start>
          <div class="flex flex-row gap-2">
            <SearchBar v-model:search-text="searchText" show-refresh @input="onFilterTextBoxChanged" @refresh="populateUsers"></SearchBar>
            <ToolbarButtons label="User" :show-add="userPermissions.administrator" @add="toggleMode('Add')"></ToolbarButtons>
          </div>
        </template>
        <template #center
          ><div class="flex flex-row gap-3">
            <div v-for="filter in filters" :key="filter.name" class="flex items-center">
              <RadioButton v-model="selectedFilter" :inputId="filter.name" name="dynamic" :value="filter.name" @change="gridApi?.onFilterChanged()" />
              <label :for="filter.name" class="ml-1 text-sm">{{ filter.name }} ({{ filter.amount }})</label>
            </div>
          </div>
        </template>
        <template #end>
          <SplitButton
            v-if="userPermissions.administrator"
            :label="selectedUser ? selectedUser.userName : 'Selected User'"
            severity="surface"
            menuButtonIcon="pi pi-cog text-xl"
            :model="menuItems"
            :disabled="!selectedUser"
            @click="toggleDisplay(true)"
          />
        </template>
      </Toolbar>
      <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
          theme="legacy"
          :rowData="users"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          pagination
          :paginationPageSize="50"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          :isExternalFilterPresent="isExternalFilterPresent"
          :doesExternalFilterPass="doesExternalFilterPass"
          rowSelection="single"
          @row-selected="selectUser"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
    </div>
    <TSDialog
      v-if="mode"
      :label="mode + ' User'"
      v-model:visible="displayModal"
      modal
      size="w-[900px]!"
      v-on:after-hide="modalClose()"
      :icon="mode === 'Add' ? 'user-plus' : 'user-edit'"
    >
      <template #content>
        <MaintainUser :user="mode !== 'Add' ? selectedUser : undefined" @submitted="toggleMode('')" />
      </template>
    </TSDialog>
  </Page>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { UserDetailListSchema, UserPermissionsSchema, type UserDetail, type UserPermissions } from "@/types/User";
import CustomBooleanIconCellRenderer from "@/components/cell-renderers/CustomBooleanIconCellRenderer.vue";
import type { ApiError } from "@/types/Response";
import { useConfirm } from "primevue/useconfirm";
import type { MenuItem } from "primevue/menuitem";
import CustomTagRenderer from "@/components/cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import type {
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  IRowNode,
  RowDataUpdatedEvent,
  RowSelectedEvent,
  ValueGetterParams,
} from "ag-grid-community";
import { getUserPermissions } from "@/services/Permissions";
import { useAuthStore } from "@/stores/auth";

const mode = ref("");
const displayModal = ref(false);
const display = ref(false);

const gridApi = ref<GridApi>();
const searchText = ref("");

const resetConfirm = useConfirm();

const users = ref<UserDetail[]>([]);
const selectedUser = ref<UserDetail>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));

const selectedFilter = ref<"Admin" | "Portal" | "All">("All");

const filters = ref([
  { name: "All", amount: 0 },
  { name: "Admin", amount: 0 },
  { name: "Portal", amount: 0 },
]);

//TODO: Import users

const menuItems: MenuItem[] = [
  {
    label: "Edit",
    icon: "pi pi-user-edit",
    visible: () => userPermissions.value.administrator,
    command: () => toggleMode("Edit"),
  },
  {
    label: "Reset Credentials",
    icon: "pi pi-send",
    visible: () =>
      (selectedUser.value ? selectedUser.value.lastLogin == null || selectedUser.value.locked : false) && userPermissions.value.administrator,
    command: () => confirmReset(),
  },
  {
    label: "Activate",
    icon: "pi pi-power-off",
    visible: () => !selectedUser.value?.active && userPermissions.value.administrator,
    command: () => toggleUserActive(true),
  },
  {
    label: "Deactivate",
    icon: "pi pi-power-off",
    visible: () => (selectedUser.value ? selectedUser.value.active : false) && userPermissions.value.administrator,
    command: () => toggleUserActive(false),
  },
];

onMounted(async (): Promise<void> => {
  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  await populateUsers();
});

const populateUsers = async (): Promise<void> => {
  await axios
    .get("/users")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(UserDetailListSchema, data, users);
          calculateTotals();
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const toggleMode = (value: string): void => {
  mode.value = value;
  displayModal.value = value.length !== 0;

  if (value.length === 0) {
    populateUsers();
    selectedUser.value = undefined;
  }
};

const modalClose = (): void => {
  toggleMode("");
  populateUsers();
};

const toggleDisplay = (show: boolean): void => {
  display.value = show;
};

const selectUser = (event: RowSelectedEvent): void => {
  let node = event.node;

  if (node.isSelected()) {
    selectedUser.value = node.data;
  }
};

const confirmReset = (): void => {
  const header = "Reset User Credentials?";
  const message = "Would you like to reset this user's password and notify them of their new credentials via the email on record?";

  confirmPopup(
    resetConfirm,
    header,
    message,
    () => resetCredentials(),
    () => {}
  );
};

const resetCredentials = (): void => {
  if (!selectedUser.value) return;

  axios
    .post(`/users/${selectedUser.value.userId}/reset`, {})
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "User credentials reset");
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const toggleUserActive = (active: boolean): void => {
  axios
    .post("/users/toggle?id=" + selectedUser.value?.userId + "&active=" + active)
    .then((response) => {
      populateUsers();
      success("Success", active ? "User activated!" : "User deactivated!");
    })
    .catch((e) => errorApi(e));
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const onFilterTextBoxChanged = (): void => {
  gridApi.value?.setGridOption("quickFilterText", searchText.value);
};

const isExternalFilterPresent = (): boolean => {
  return selectedFilter.value !== "All";
};

const doesExternalFilterPass = (node: IRowNode<UserDetail>): boolean => {
  if (node.data == null) return false;
  const userDetail: UserDetail = node.data;

  switch (selectedFilter.value) {
    case "Admin":
      return userDetail.user;
    case "Portal":
      return !userDetail.user;
    default:
      return true;
  }
};

const calculateTotals = (): void => {
  let admin = 0;
  let portal = 0;

  users.value?.forEach((user: UserDetail) => {
    if (user.user) {
      admin++;
    } else {
      portal++;
    }
  });

  filters.value.forEach((value) => {
    if (value.name === "Admin") value.amount = admin;
    else if (value.name === "Portal") value.amount = portal;
    else if (value.name === "All") value.amount = users.value != null ? users.value.length : 0;
  });
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    headerName: "Site Access",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
    valueGetter: (params: ValueGetterParams) => (params.data.user ? "Admin" : "Portal"),
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.USER_TYPE,
    },
  },
  {
    field: "userName",
    headerName: "Username",
    width: 100,
  },
  {
    headerName: "Group",
    width: 100,
    valueGetter: (params: ValueGetterParams) => params.data.userGroup?.name,
  },
  {
    field: "lastLogin",
    headerName: "Last Login",
    width: 100,
  },
  {
    field: "lastFailedLogin",
    headerName: "Last Failed Login",
    width: 100,
  },
  {
    field: "active",
    headerName: "Active",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "locked",
    headerName: "Locked",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
]);
</script>
